//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ReactNode, MouseEvent } from 'react';
import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  DialogActions,
  DialogTitle,
  DialogContent,
  Button,
  ButtonProps,
  styled,
  IconButton
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PolarisIcon from './PolarisIcon';

type DialogProps = {
  children: ReactNode;
  title: string;
  open: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  submitText?: string;
  cancelText?: string;
  titleIcon?: ReactNode;
  maxWidth?: number;
  loading?: boolean;
  buttonColor?: ButtonProps['color'];
  className?: string;
  hideCancel?: boolean;
  showButtons?: boolean;
  showX?: boolean;
  isSubmitDisabled?: boolean;
};

const Dialog = ({
  children,
  title,
  open,
  onCancel,
  onSubmit,
  submitText = 'Submit',
  cancelText = 'Cancel',
  titleIcon,
  maxWidth = 600,
  loading = false,
  buttonColor,
  className,
  hideCancel,
  showButtons = true,
  showX = false,
  isSubmitDisabled = false
}: DialogProps) => {
  const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCancel();
  };

  const handleSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    if (event) event.stopPropagation();
    onSubmit();
  };

  return (
    <MuiDialog
      open={open}
      onClose={onCancel as MuiDialogProps['onClose']}
      aria-labelledby="form-dialog-title"
      onClick={(e) => e.stopPropagation()}
      {...{ className }}
    >
      <Title id="form-dialog-title">
        {titleIcon && titleIcon}
        <div style={{ flexGrow: 1 }}>{title}</div>
        {showX && (
          <IconButton onClick={handleCancel}>
            <PolarisIcon icon="close" />
          </IconButton>
        )}
      </Title>
      <Content sx={{ maxWidth }}>{children}</Content>
      {showButtons && (
        <Actions>
          {!hideCancel && (
            <Button color="default" onClick={handleCancel}>
              {cancelText}
            </Button>
          )}
          <LoadingButton
            onClick={handleSubmit}
            type="submit"
            color={buttonColor || 'primary'}
            variant="contained"
            loading={loading}
            disabled={isSubmitDisabled}
          >
            {submitText}
          </LoadingButton>
        </Actions>
      )}
    </MuiDialog>
  );
};

export default Dialog;

const Title = styled(DialogTitle)`
  display: flex;
  align-items: center;
`;

const Content = styled(DialogContent)`
  min-width: 350px;
  & form {
    margin-top: ${({ theme }) => theme.spacing(1)};
  }
`;

const Actions = styled(DialogActions)`
  margin: ${({ theme }) => theme.spacing(2, 2, 1)};
`;
